import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Table, TableBody,
  TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useToast } from '../../context/ToastContext';
import { GroupAccessService } from '../../services/groupAccess.service';
import { RoleService } from '../../services/role.service';
import { GroupAccess, GroupAccessCandidate } from '../../interfaces/intercompany.interface';
import { IRole } from '../../interfaces/role.interface';
import { SearchableSelect } from '../common/SearchableSelect';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { helpWhy } from '../../help/why';

interface Props {
  open: boolean;
  onClose: () => void;
  /** The institution the access is granted IN (the user gets a role here). */
  institutionId: string;
  institutionName?: string;
}

const candidateLabel = (c: GroupAccessCandidate) => `${c.fullName || c.username} (${c.username}) - ${c.sourceInstitutionName}`;

/**
 * "Members from group companies": people from sister companies of the same institution group who hold a role in this
 * institution. Granting is additive (nothing the person already has is removed) and needs the administrator role in both
 * companies; revoking removes only the access granted here.
 */
const GroupAccessDialog: React.FC<Props> = ({ open, onClose, institutionId, institutionName }) => {
  const toast = useToast();
  const [grants, setGrants] = useState<GroupAccess[]>([]);
  const [candidates, setCandidates] = useState<GroupAccessCandidate[]>([]);
  const [roles, setRoles] = useState<IRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState('');
  const [roleId, setRoleId] = useState('');
  const [saving, setSaving] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<GroupAccess | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [g, c, r] = await Promise.all([
        GroupAccessService.list(institutionId), GroupAccessService.candidates(institutionId), RoleService.list(institutionId),
      ]);
      setGrants(g);
      setCandidates(c);
      setRoles(r.filter(x => x.isActive));
    } catch (e) {
      toast.showApiError(e, 'Failed to load group-company access');
    } finally {
      setLoading(false);
    }
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) {
      setUserId('');
      setRoleId('');
      load();
    }
  }, [open, load]);

  const candidate = candidates.find(c => c.userId === userId);

  const grant = async () => {
    if (!candidate || !roleId) return;
    try {
      setSaving(true);
      await GroupAccessService.grant({
        institutionId, sourceInstitutionId: candidate.sourceInstitutionId, userId: candidate.userId, roleId,
      });
      toast.showSuccess(`${candidate.fullName || candidate.username} now has access here. They must sign in again to see it.`);
      setUserId('');
      setRoleId('');
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to grant access');
    } finally {
      setSaving(false);
    }
  };

  const revoke = async () => {
    if (!revokeTarget) return;
    try {
      await GroupAccessService.revoke(institutionId, revokeTarget.membershipId);
      toast.showSuccess('Access revoked');
      setRevokeTarget(null);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to revoke access');
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-help-page="institutions.groupAccess.page">
        <DialogTitle>Members from group companies{institutionName ? ` - ${institutionName}` : ''}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Give a person from a sister company a role here without moving them. Their existing access stays as it is. You need the
            administrator role in both companies (the platform administrator can do it for any group company).
          </Alert>
          {loading ? <CircularProgress /> : (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }} data-help-section="institutions.groupAccess.grant">
                <Box sx={{ flex: '2 1 280px', maxWidth: 480 }} data-help="institutions.groupAccess.person">
                  <SearchableSelect options={candidates} value={userId} onChange={id => setUserId(id)} label="Person from a sister company"
                    size="small" getOptionId={c => c.userId} getOptionLabel={candidateLabel}
                    noOptionsText="No people available (you must administer the sister company too)" />
                </Box>
                <Box sx={{ flex: '1 1 220px', maxWidth: 320 }} data-help="institutions.groupAccess.role">
                  <SearchableSelect options={roles} value={roleId} onChange={id => setRoleId(id)} label="Role here" size="small"
                    getOptionLabel={r => r.name} />
                </Box>
                <Button variant="contained" disabled={!userId || !roleId || saving} onClick={grant} data-help="institutions.groupAccess.grantButton" {...helpWhy(!saving && (!userId || !roleId) ? { kind: 'status', message: 'Pick a person and a role first.' } : null)}>Grant access</Button>
              </Box>
              <Typography variant="subtitle2" gutterBottom>Current access from group companies</Typography>
              <Table size="small" data-help-section="institutions.groupAccess.table">
                <TableHead>
                  <TableRow><TableCell data-help="institutions.groupAccess.col.person">Person</TableCell><TableCell data-help="institutions.groupAccess.col.home">Home company</TableCell><TableCell data-help="institutions.groupAccess.col.roles">Roles here</TableCell><TableCell data-help="institutions.groupAccess.col.actions" /></TableRow>
                </TableHead>
                <TableBody>
                  {grants.length === 0 && <TableRow><TableCell colSpan={4}>Nobody from a group company has access here yet.</TableCell></TableRow>}
                  {grants.map(g => (
                    <TableRow key={g.membershipId}>
                      <TableCell>{g.fullName || g.username}{g.fullName && g.username ? ` (${g.username})` : ''}</TableCell>
                      <TableCell>{g.sourceInstitutionName}</TableCell>
                      <TableCell>{g.roles.length === 0 ? '-' : g.roles.map(r => <Chip key={r.id} size="small" sx={{ mr: 0.5 }} label={r.name} />)}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" data-help="institutions.groupAccess.action.revoke" aria-label={`Revoke access of ${g.fullName || g.username}`} onClick={() => setRevokeTarget(g)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={onClose} data-help="institutions.groupAccess.close">Close</Button></DialogActions>
      </Dialog>
      <ConfirmDialog
        open={!!revokeTarget}
        title="Revoke access"
        message={`Remove ${revokeTarget?.fullName || revokeTarget?.username || ''}'s access to this company? Their access in their own company is not affected.`}
        confirmLabel="Revoke"
        confirmColor="error"
        onConfirm={revoke}
        onCancel={() => setRevokeTarget(null)}
      />
    </>
  );
};

export default GroupAccessDialog;
